import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CognitiveScenario } from '@/types/assessment';

interface CognitiveStyleScreenProps {
  scenarios: CognitiveScenario[];
  selections: string[];
  onToggle: (id: string) => void;
}

const CognitiveStyleScreen = ({ scenarios, selections, onToggle }: CognitiveStyleScreenProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Adaptive: show scenarios based on previous answers
  const answeredCount = selections.length;
  const currentScenario = scenarios[currentIndex];

  if (!currentScenario) {
    return (
      <div className="glass-card p-6 text-center space-y-3">
        <span className="text-4xl">🧠</span>
        <h3 className="font-display font-semibold text-primary">Cognitive Profile Complete</h3>
        <p className="text-sm text-muted-foreground">
          You've answered all {answeredCount} scenarios. Your thinking style is being mapped.
        </p>
        <div className="flex justify-center gap-2 mt-4">
          {scenarios.map((s, i) => {
            const answered = selections.find(sel => sel.startsWith(s.id));
            const trait = answered?.split(':')[1];
            return (
              <div
                key={s.id}
                className={`w-3 h-3 rounded-full ${
                  trait === 'a' ? 'bg-primary' : trait === 'b' ? 'bg-accent' : 'bg-muted'
                }`}
              />
            );
          })}
        </div>
      </div>
    );
  }

  const handleSelect = (option: 'a' | 'b') => {
    onToggle(`${currentScenario.id}:${option}`);
    // Move to next scenario after a brief delay
    setTimeout(() => {
      if (currentIndex < scenarios.length - 1) {
        setCurrentIndex(currentIndex + 1);
      }
    }, 300);
  };

  const alreadyAnswered = selections.find(s => s.startsWith(currentScenario.id));

  return (
    <div className="space-y-6">
      {/* Progress indicator */}
      <div className="flex items-center gap-2">
        {scenarios.map((_, i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-all ${
              i < currentIndex ? 'bg-primary' : i === currentIndex ? 'bg-primary/50' : 'bg-muted'
            }`}
          />
        ))}
      </div>

      <p className="text-xs text-muted-foreground text-center">
        Scenario {currentIndex + 1} of {scenarios.length} — Your answers shape the next question
      </p>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentScenario.id}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="space-y-4"
        >
          <div className="glass-card p-5 space-y-2">
            <p className="text-sm text-muted-foreground italic">Imagine this...</p>
            <p className="text-foreground font-medium leading-relaxed">{currentScenario.scenario}</p>
          </div>

          <div className="grid gap-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleSelect('a')}
              className={`p-4 rounded-xl border text-left transition-all ${
                alreadyAnswered?.endsWith(':a')
                  ? 'bg-primary/20 border-primary text-primary'
                  : 'bg-secondary border-border text-foreground hover:border-primary/50'
              }`}
            >
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Option A</span>
              <p className="text-sm font-medium mt-1">{currentScenario.optionA.label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Trait: {currentScenario.optionA.trait}</p>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleSelect('b')}
              className={`p-4 rounded-xl border text-left transition-all ${
                alreadyAnswered?.endsWith(':b')
                  ? 'bg-accent/20 border-accent text-accent'
                  : 'bg-secondary border-border text-foreground hover:border-accent/50'
              }`}
            >
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Option B</span>
              <p className="text-sm font-medium mt-1">{currentScenario.optionB.label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Trait: {currentScenario.optionB.trait}</p>
            </motion.button>
          </div>
        </motion.div>
      </AnimatePresence>

      {currentIndex > 0 && (
        <button
          onClick={() => setCurrentIndex(currentIndex - 1)}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          ← Revisit previous
        </button>
      )}
    </div>
  );
};

export default CognitiveStyleScreen;
